import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const Employees = () => {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState({}); 
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [showActive, setShowActive] = useState(true);
  const [roleError, setRoleError] = useState('');
  const [roles, setRoles] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [leaveUpdateLoading, setLeaveUpdateLoading] = useState(false);
  const [leaveUpdateResult, setLeaveUpdateResult] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualBalances, setManualBalances] = useState({});

  const currentUserId = localStorage.getItem('id');
  const navigate = useNavigate();

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    axiosInstance.get('/user/')
      .then(res => setUsers(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get(`/user/${currentUserId}/`)
      .then(res => {
        const userData = res.data;
        setCurrentUser({ 
          ...userData, 
          role: userData.role?.name || userData.role?.[0]?.name || 'guest' 
        });
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRoles([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, []);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: roles[1].id });
      navigate('/user-dashboard');
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  const filteredEmployees = users.filter((employee) => {
    const fullName = `${employee.first_name} ${employee.last_name}`.toLowerCase();
    return (
      fullName.includes(searchTerm.toLowerCase()) || 
      employee.employee_code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleDelete = (userId) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      axiosInstance.delete(`/user/${userId}/`)
        .then(() => setUsers(users.filter(u => u.id !== userId)))
        .catch(console.error);
    }
  };

  const handleToggleFreePunch = (userId, currentStatus) => {
    axiosInstance.patch(`/users/${userId}/free-punch/`, {
      is_free_punch: !currentStatus
    })
    .then(res => {
      setUsers(users.map(u => u.id === userId ? { ...u, is_free_punch: res.data.is_free_punch } : u));
    })
    .catch(console.error);
  };

  const handleDisableUser = (userId) => {
    if (window.confirm('Are you sure you want to disable this employee?')) {
      axiosInstance.patch(`/user/${userId}/disable/`)
        .then(() => {
          setUsers(users.map(u => u.id === userId ? { ...u, is_active: false } : u));
        })
        .catch(console.error);
    }
  };

  const handleUpdateLeaveBalance = (user) => {
    setSelectedUser(user);
    setLeaveUpdateResult(null);
    setManualMode(false);
    setManualBalances(user.leave_balances || {});
    setShowLeaveModal(true);
  };

  const handleConfirmLeaveUpdate = async () => {
    if (!selectedUser) return;
    
    setLeaveUpdateLoading(true);
    try {
      if (manualMode) {
        // Manual update
        const response = await axiosInstance.post('/manual-leave-balance/', {
          user_id: selectedUser.id,
          leave_balances: manualBalances
        });
        
        setLeaveUpdateResult(response.data);
        
        // Refresh user data to get updated balances
        const updatedUsers = await axiosInstance.get('/user/');
        setUsers(updatedUsers.data);
        
        // Close modal after successful update
        setTimeout(() => {
          setShowLeaveModal(false);
          setSelectedUser(null);
          setLeaveUpdateResult(null);
          setManualMode(false);
          setManualBalances({});
        }, 2000);
      } else {
        // Automatic accrual
        const response = await axiosInstance.post('/accrue-leaves/', {
          user_id: selectedUser.id,
          dry_run: false
        });
        
        setLeaveUpdateResult(response.data);
        
        // Refresh user data to get updated balances
        const updatedUsers = await axiosInstance.get('/user/');
        setUsers(updatedUsers.data);
        
        // Close modal after successful update
        setTimeout(() => {
          setShowLeaveModal(false);
          setSelectedUser(null);
          setLeaveUpdateResult(null);
        }, 2000);
      }
      
    } catch (error) {
      console.error('Error updating leave balance:', error);
      setLeaveUpdateResult({ error: error.response?.data?.error || 'Failed to update leave balance' });
    } finally {
      setLeaveUpdateLoading(false);
    }
  };

  const handleManualBalanceChange = (leaveType, value) => {
    setManualBalances({
      ...manualBalances,
      [leaveType]: parseFloat(value) || 0
    });
  };

  const handleLogout = () => {
    axiosInstance.post('/logout/', {}).finally(() => {
      localStorage.clear();
      window.location.href = '/';
    });
  };

  const activeEmployees = filteredEmployees.filter(e => e.is_active);
  const disabledEmployees = filteredEmployees.filter(e => !e.is_active);

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={currentUser} 
        activeRole={currentUser?.role}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout} 
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <div className="header-row">
            <div>
              <h1>👥 Employees</h1>
              <p>Manage employee records</p>
            </div>
            <Link to="/create-users" className="action-btn">
              ➕ Create User
            </Link>
          </div>
        </div>

        <div className="dashboard-card full-width">
          <div className="card-header">
            <span className="card-icon">👥</span>
            <h3>Employee List</h3>
          </div>
          <div className="card-body">
            <div className="filter-bar">
              <input
                type="text"
                className="search-input"
                placeholder="Search by name or employee code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="tab-buttons">
                <button 
                  className={`tab-btn ${showActive ? 'active' : ''}`}
                  onClick={() => setShowActive(true)}
                >
                  Active ({activeEmployees.length})
                </button>
                <button 
                  className={`tab-btn ${!showActive ? 'active' : ''}`}
                  onClick={() => setShowActive(false)}
                >
                  Disabled ({disabledEmployees.length})
                </button>
              </div>
            </div>

            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Employee Code</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Manager</th>
                    <th>Role</th>
                    <th>Branch</th>
                    <th>Leave Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(showActive ? activeEmployees : disabledEmployees).map(employee => (
                    <tr key={employee.id}>
                      <td>
                        <div className="person-avatar small">
                          {employee.first_name?.[0]}{employee.last_name?.[0]}
                        </div>
                      </td>
                      <td>{employee.employee_code}</td>
                      <td>{employee.first_name} {employee.last_name}</td>
                      <td>{employee.contact_number || '—'}</td>
                      <td>{employee.manager_name || '—'}</td>
                      <td>
                        <span className="role-badge">{employee.role_name || 'N/A'}</span>
                      </td>
                      <td>{employee.work_location_name || '—'}</td>
                      <td>
                        <div className="leave-balance-mini">
                          {employee.leave_balances ? (
                            Object.entries(employee.leave_balances).map(([type, balance]) => (
                              <span key={type} className="balance-tag">
                                {type}: {parseFloat(balance).toFixed(1)}
                              </span>
                            ))
                          ) : (
                            <span className="no-balance">—</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Link to={`/assign/${employee.id}`} className="icon-btn" title="Assign">
                            🏷️
                          </Link>
                          <Link to={`/update-user/${employee.id}`} className="icon-btn" title="Edit">
                            ✏️
                          </Link>
                          <Link to={`/user/${employee.id}`} className="icon-btn" title="View">
                            👁️
                          </Link>
                          <button 
                            className="icon-btn" 
                            title={employee.is_free_punch ? "Free Punch On" : "Free Punch Off"}
                            onClick={() => handleToggleFreePunch(employee.id, employee.is_free_punch)}
                          >
                            🎫
                          </button>
                          <button 
                            className="icon-btn" 
                            title="Update Leave Balance"
                            onClick={() => handleUpdateLeaveBalance(employee)}
                          >
                            🔄
                          </button>
                          {showActive && ['admin', 'manager'].includes(currentUser?.role_name) && (
                            <>
                              <button 
                                className="icon-btn" 
                                title="Disable"
                                onClick={() => handleDisableUser(employee.id)}
                              >
                                🔒
                              </button>
                              <button 
                                className="icon-btn delete" 
                                title="Delete"
                                onClick={() => handleDelete(employee.id)}
                              >
                                🗑️
                              </button>
                            </>
                          )}
                          {!showActive && ['admin', 'manager'].includes(currentUser?.role_name) && (
                            <button 
                              className="icon-btn delete" 
                              title="Delete"
                              onClick={() => handleDelete(employee.id)}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(showActive ? activeEmployees : disabledEmployees).length === 0 && (
                <div className="empty-state">No employees found</div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Leave Balance Update Modal */}
      {showLeaveModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowLeaveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Leave Balance</h3>
              <button className="modal-close" onClick={() => setShowLeaveModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-info-section">
                <div className="user-avatar">
                  {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                </div>
                <div className="user-details">
                  <h4>{selectedUser.first_name} {selectedUser.last_name}</h4>
                  <p>{selectedUser.employee_code} • {selectedUser.work_location_name || 'No Branch'}</p>
                </div>
              </div>

              <div className="update-mode-toggle">
                <button 
                  className={`mode-btn ${!manualMode ? 'active' : ''}`}
                  onClick={() => setManualMode(false)}
                >
                  🔄 Auto Accrual
                </button>
                <button 
                  className={`mode-btn ${manualMode ? 'active' : ''}`}
                  onClick={() => setManualMode(true)}
                >
                  ✏️ Manual Update
                </button>
              </div>

              {!manualMode ? (
                <div className="balance-comparison">
                  <h4>Current Leave Balances</h4>
                  {selectedUser.leave_balances ? (
                    <div className="balance-grid">
                      {Object.entries(selectedUser.leave_balances).map(([type, balance]) => (
                        <div key={type} className="balance-item">
                          <span className="balance-type">{type}</span>
                          <span className="balance-amount">{parseFloat(balance).toFixed(2)} days</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-balance-message">No leave balances found</p>
                  )}
                  <p className="mode-description">
                    This will automatically calculate and add monthly leave accrual based on the defined rates.
                  </p>
                </div>
              ) : (
                <div className="manual-balance-section">
                  <h4>Manual Balance Update</h4>
                  <p className="mode-description">
                    Enter the exact leave balances you want to set for this employee.
                  </p>
                  <div className="manual-balance-form">
                    {selectedUser.leave_balances ? (
                      Object.entries(selectedUser.leave_balances).map(([type, balance]) => (
                        <div key={type} className="balance-input-group">
                          <label>{type} Balance</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={manualBalances[type] !== undefined ? manualBalances[type] : balance}
                            onChange={(e) => handleManualBalanceChange(type, e.target.value)}
                            className="balance-input"
                          />
                          <span className="current-value">Current: {parseFloat(balance).toFixed(2)} days</span>
                        </div>
                      ))
                    ) : (
                      <p className="no-balance-message">No existing leave balances to edit</p>
                    )}
                  </div>
                </div>
              )}

              {leaveUpdateResult && (
                <div className={`update-result ${leaveUpdateResult.error ? 'error' : 'success'}`}>
                  {leaveUpdateResult.error ? (
                    <p>❌ {leaveUpdateResult.error}</p>
                  ) : (
                    <>
                      <p>✅ Leave balance updated successfully!</p>
                      {leaveUpdateResult.updated_users && leaveUpdateResult.updated_users.length > 0 && (
                        <div className="updated-balances">
                          <h5>New Balances:</h5>
                          {Object.entries(leaveUpdateResult.updated_users[0].new_balances).map(([type, balance]) => (
                            <div key={type} className="updated-balance-item">
                              <span>{type}:</span>
                              <span>{parseFloat(balance).toFixed(2)} days</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowLeaveModal(false)}
                disabled={leaveUpdateLoading}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleConfirmLeaveUpdate}
                disabled={leaveUpdateLoading}
              >
                {leaveUpdateLoading ? 'Updating...' : manualMode ? 'Apply Manual Update' : 'Update Balance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
